import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
  OnInit,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { CommonModule } from "@angular/common";
import {
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { DateAdapter, MAT_DATE_FORMATS } from "@angular/material/core";
import { MatDatepickerModule } from "@angular/material/datepicker";
import {
  MatDialogModule,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatSelectModule } from "@angular/material/select";
import { finalize } from "rxjs/operators";

import { formatDateOnly } from "@lib/utils/date";
import { getErrorMessage } from "@app/lib/utils/error";
import { NotificationService } from "@app/shared/services/notification.service";
import {
  AppDateAdapter,
  APP_DATE_FORMATS,
} from "@shared/date/app-date-adapter";
import {
  UploadDocumentDto,
  ModuleType,
  UploadDocumentResponseDto,
} from "@shared/models";
import { DocumentsApiService } from "@shared/services/documents-api.service";
import { VendorDocumentUploadDto } from "@app/features/orders/models";
import { VendorOrderApiService } from "@app/features/orders/services/vendor-order-api.service";

interface UploadedFile {
  file: File;
  progress: number;
  docType: FormControl<string | null>;
  isUploading?: boolean;
  uploadError?: string;
  uploadSuccess?: boolean;
  serverMessage?: string;
}

export type UploadDocumentDialogMode = "documents" | "vendorManualUpload";

export interface UploadDocumentDialogData {
  ModuleType: ModuleType;
  sourceId: number;
  eventId: number;
  mode?: UploadDocumentDialogMode;
}

const DOCUMENT_UPLOAD_SUCCESS_MESSAGE = "Document created successfully";
const VENDOR_UPLOAD_SUCCESS_MESSAGE = "Report uploaded successfully";
const VENDOR_UPLOAD_ERROR_MESSAGE = "Upload failed. Please try again.";
const INVALID_FILE_MESSAGE =
  "Invalid file. Please upload a PDF, XML, or Doc file up to 200MB.";
const VENDOR_DATE_REQUIRED_MESSAGE =
  "Vendor Order Completed Date is required when a Client Order Completed Date is entered.";

@Component({
  selector: "app-upload-document-dialog",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDatepickerModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressBarModule,
    MatSelectModule,
  ],
  templateUrl: "./upload-document-dialog.component.html",
  styleUrl: "./upload-document-dialog.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: DateAdapter, useClass: AppDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: APP_DATE_FORMATS },
  ],
})
export class UploadDocumentDialogComponent implements OnInit {
  private readonly ref = inject(MatDialogRef<UploadDocumentDialogComponent>);
  private readonly fb = inject(FormBuilder);
  private readonly documentsApiService = inject(DocumentsApiService);
  private readonly vendorOrderApiService = inject(VendorOrderApiService);
  private readonly notificationService = inject(NotificationService);
  readonly data = inject<UploadDocumentDialogData>(MAT_DIALOG_DATA);

  protected readonly isVendorManualUploadMode =
    this.data.mode === "vendorManualUpload";
  
  /** Vendor mode only — both dates are optional and blank by default. */
  protected readonly completedDatesForm = this.fb.group({
    vendorOrderCompletedDate: this.fb.control<Date | null>(null),
    clientOrderCompletedDate: this.fb.control<Date | null>(null),
  });

  private readonly vendorCompletedDate = signal<Date | null>(null);
  private readonly clientCompletedDate = signal<Date | null>(null);

  protected readonly isVendorDateMissing = computed(
    () =>
      this.clientCompletedDate() !== null &&
      this.vendorCompletedDate() === null,
  );

  protected readonly vendorDateRequiredMessage = VENDOR_DATE_REQUIRED_MESSAGE;

  protected readonly files = signal<UploadedFile[]>([]);
  protected readonly isDragging = signal(false);
  protected readonly isUploading = signal(false);
  protected readonly documentTypeOptions = signal<string[]>([]);
  protected readonly isLoadingDocumentTypes = signal(false);
  protected readonly documentTypesError = signal(false);

  private readonly maxFileSize = 200 * 1024 * 1024;
  private readonly allowedTypes = [
    "application/pdf",
    "application/xml",
    "text/xml",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ];

  constructor() {
    this.completedDatesForm.controls.vendorOrderCompletedDate.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((value) => this.vendorCompletedDate.set(value));

    this.completedDatesForm.controls.clientOrderCompletedDate.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((value) => this.clientCompletedDate.set(value));
  }

  ngOnInit(): void {
    if (!this.isVendorManualUploadMode) {
      this.loadDocumentTypes();
    }
  }

  private loadDocumentTypes(): void {
    this.isLoadingDocumentTypes.set(true);
    this.documentTypesError.set(false);

    this.documentsApiService.getDocumentTypes().subscribe({
      next: (types) => {
        this.documentTypeOptions.set(types);
        this.isLoadingDocumentTypes.set(false);
      },
      error: (error) => {
        console.error("Failed to load document types:", error);
        this.documentTypesError.set(true);
        this.isLoadingDocumentTypes.set(false);
        this.notificationService.error(
          getErrorMessage(
            error,
            "Failed to load document types. Please try again.",
          ),
        );
      },
    });
  }

  protected onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const selectedFiles = input?.files;
    if (selectedFiles) {
      this.processFiles(Array.from(selectedFiles));
    }
  }

  protected onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(true);
  }

  protected onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
  }

  protected onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
    const droppedFiles = event.dataTransfer?.files;
    if (droppedFiles) {
      this.processFiles(Array.from(droppedFiles));
    }
  }

  private processFiles(fileList: File[]): void {
    const validFiles = fileList.filter(
      (file) =>
        file.size <= this.maxFileSize && this.allowedTypes.includes(file.type),
    );

    if (
      this.isVendorManualUploadMode &&
      validFiles.length !== fileList.length
    ) {
      this.notificationService.error(INVALID_FILE_MESSAGE);
    }

    const newFiles: UploadedFile[] = validFiles.map((file) => ({
      file,
      progress: 0,
      docType: new FormControl<string | null>(null, Validators.required),
    }));

    if (this.isVendorManualUploadMode) {
      const [singleFile] = newFiles;
      if (!singleFile) {
        return;
      }
      this.files.set([singleFile]);
      this.simulateProgress(singleFile);
      return;
    }

    this.files.update((current) => [...current, ...newFiles]);

    newFiles.forEach((uploadedFile) => {
      this.simulateProgress(uploadedFile);
    });
  }

  private simulateProgress(uploadedFile: UploadedFile): void {
    const interval = setInterval(() => {
      if (uploadedFile.progress >= 100) {
        clearInterval(interval);
        return;
      }
      uploadedFile.progress += 10;
      this.files.set([...this.files()]);
    }, 200);
  }

  protected upload(): void {
    if (!this.canUpload || this.isUploading()) {
      return;
    }

    this.isUploading.set(true);
    const moduleType = this.data.ModuleType;
    const filesToUpload = this.files().filter((f) => !f.uploadSuccess);

    if (filesToUpload.length === 0) {
      this.isUploading.set(false);
      this.ref.close(true);
      return;
    }

    if (this.isVendorManualUploadMode) {
      this.uploadVendorDocument(filesToUpload[0]);
      return;
    }

    const uploadDtos: UploadDocumentDto[] = filesToUpload.map(
      (uploadedFile): UploadDocumentDto => {
        const orderId =
          moduleType === ModuleType.ORDER || moduleType === ModuleType.EVENT
            ? this.data.sourceId
            : null;
        const loanId =
          moduleType === ModuleType.LOAN ? this.data.sourceId : null;
        const eventId =
          moduleType === ModuleType.EVENT ? this.data.eventId : null;

        return {
          file: uploadedFile.file,
          documentType: uploadedFile.docType.value!,
          description: "",
          moduleType: moduleType,
          orderId: orderId,
          loanId: loanId,
          invoiceId: null,
          eventId: eventId,
          source: "",
        };
      },
    );

    this.documentsApiService
      .bulkUploadDocuments(uploadDtos)
      .pipe(finalize(() => this.isUploading.set(false)))
      .subscribe({
        next: (results: UploadDocumentResponseDto[]) => {
          this.handleUploadResults(results, filesToUpload);
        },
        error: (error) => {
          this.notificationService.error(
            getErrorMessage(error, "Upload failed. Please try again."),
          );
        },
      });
  }

  private uploadVendorDocument(uploadedFile: UploadedFile): void {
    const request: VendorDocumentUploadDto = {
      file: uploadedFile.file,
      orderId: this.data.sourceId,
      ...this.completedDatesPayload(),
    };

    this.completedDatesForm.disable({ emitEvent: false });

    this.vendorOrderApiService
      .uploadVendorDocument(request)
      .pipe(
        finalize(() => {
          this.isUploading.set(false);
          this.completedDatesForm.enable({ emitEvent: false });
        }),
      )
      .subscribe({
        next: () => {
          uploadedFile.uploadSuccess = true;
          uploadedFile.uploadError = undefined;
          uploadedFile.serverMessage = undefined;
          this.files.set([...this.files()]);

          this.notificationService.success(VENDOR_UPLOAD_SUCCESS_MESSAGE);
          this.ref.close(true);
        },
        error: (error) => {
          const message = getErrorMessage(error, VENDOR_UPLOAD_ERROR_MESSAGE);

          uploadedFile.uploadSuccess = false;
          uploadedFile.uploadError = this.transformErrorMessage(message);
          uploadedFile.serverMessage = message;
          this.files.set([...this.files()]);

          this.notificationService.error(message);
        },
      });
  }

  private completedDatesPayload(): Partial<VendorDocumentUploadDto> {
    const { vendorOrderCompletedDate, clientOrderCompletedDate } =
      this.completedDatesForm.getRawValue();

    const vendorDate = formatDateOnly(vendorOrderCompletedDate);
    const clientDate = formatDateOnly(clientOrderCompletedDate);

    return {
      ...(vendorDate ? { vendorOrderCompletedDate: vendorDate } : {}),
      ...(clientDate ? { clientOrderCompletedDate: clientDate } : {}),
    };
  }

  private handleUploadResults(
    results: UploadDocumentResponseDto[],
    uploadedFiles: UploadedFile[],
  ): void {
    results.forEach((result, index) => {
      if (index < uploadedFiles.length) {
        const file = uploadedFiles[index];
        const isSuccess = this.isDocumentUploadSuccessful(result);
        file.uploadSuccess = isSuccess;
        file.serverMessage = result.message;
        if (isSuccess) {
          file.uploadError = undefined;
        } else {
          file.uploadError = this.transformErrorMessage(result.message);
          file.progress = 0;
        }
      }
    });

    this.files.set([...this.files()]);

    const successCount = results.filter((r) =>
      this.isDocumentUploadSuccessful(r),
    ).length;
    const errorCount = results.length - successCount;

    if (errorCount === 0 && successCount > 0) {
      this.notificationService.success(
        `Successfully uploaded ${successCount} document(s)`,
      );
      this.ref.close(true);
    }
  }

  private isDocumentUploadSuccessful(
    result: UploadDocumentResponseDto,
  ): boolean {
    const hasValidId = result.id > 0;
    const hasUrl = result.url !== null && result.url !== "";
    const hasSuccessMessage =
      result.message === DOCUMENT_UPLOAD_SUCCESS_MESSAGE;
    return hasValidId && hasUrl && hasSuccessMessage;
  }

  private transformErrorMessage(errorMessage: string): string {
    if (errorMessage.toLowerCase().includes("already exists")) {
      return "Duplicate detected";
    }
    return errorMessage;
  }

  protected close(): void {
    this.ref.close(null);
  }

  protected removeFile(index: number): void {
    this.files.update((current) => current.filter((_, i) => i !== index));
  }

  protected get canUpload(): boolean {
    if (this.isVendorManualUploadMode && this.isVendorDateMissing()) {
      return false;
    }
    const files = this.files();
    if (files.length === 0) {
      return false;
    }
    const filesToUpload = files.filter((f) => !f.uploadSuccess);
    if (filesToUpload.length === 0) {
      return false;
    }
    return files.every(
      (f) =>
        (this.isVendorManualUploadMode || f.docType.valid) &&
        f.progress === 100,
    );
  }

  protected get allowsMultipleFiles(): boolean {
    return !this.isVendorManualUploadMode;
  }

  protected get showDocumentTypeSelector(): boolean {
    return !this.isVendorManualUploadMode;
  }

  protected get dialogTitle(): string {
    return this.isVendorManualUploadMode
      ? "Upload Document"
      : "Upload Documents";
  }

  protected get uploadButtonText(): string {
    if (this.isUploading()) {
      return "Uploading...";
    }
    const hasFailedUploads = this.files().some(
      (f) => f.uploadError || (f.serverMessage && !f.uploadSuccess),
    );
    return hasFailedUploads ? "Retry" : "Save";
  }
}
